import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';
import { viewAsHeaders } from '../../lib/view-as';
import { getToken, handleExpiredSession, isPublicUrl } from '../../lib/http';

// Create our baseQuery instance
const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_REST_API,
  prepareHeaders: (headers) => {
    const token = localStorage.getItem('token');
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
      // Vista previa "Ver como": el servidor decide si la concede.
      Object.entries(viewAsHeaders()).forEach(([k, v]) => headers.set(k, v));
    }
    return headers;
  },
});

/** La URL de la petición, venga como string o como objeto `{ url, method, ... }`. */
const urlOf = args => (typeof args === 'string' ? args : args?.url);

// Reintentar un 401/403 no lo va a arreglar: solo triplica la latencia del fallo
// y el ruido en observabilidad. `retry.fail` corta el reintento de inmediato.
const baseQueryWithAuthGuard = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions);
  const status = result.error?.status;
  if (status === 401 || status === 403) {
    // Un 401 aquí es una sesión caducada o revocada, igual que en axios. Antes solo se
    // cortaba el reintento: la petición fallaba en silencio y la aplicación seguía
    // pintando pantallas vacías con una sesión que ya no valía. Ahora pasa por el
    // mismo camino que `lib/http.js` — limpiar almacenamiento y volver al login.
    // Se excluyen los endpoints públicos: su 401 significa "credenciales incorrectas".
    // Misma condición que `isSessionExpiry` en axios, incluido el `getToken()`: sin
    // token no hay sesión que caducar, y redirigir ahí podría dar un bucle con la
    // propia pantalla de login.
    if (status === 401 && getToken() && !isPublicUrl(urlOf(args))) {
      handleExpiredSession();
    }
    retry.fail(result.error);
  }
  return result;
};

const baseQueryWithRetry = retry(baseQueryWithAuthGuard, { maxRetries: 2 });

export const api = createApi({
  reducerPath: 'splitApi',
  baseQuery: baseQueryWithRetry,
  tagTypes: ['User', 'Rubric', 'Camp', 'Event', 'Club', 'Director'],
  endpoints: () => ({}),
});
